import React, { useEffect, useState } from "react";
import { FormControl, FormControlLabel, InputLabel, MenuItem, Select, Stack, Switch, TextField, Typography } from "@mui/material";
import { SEGMENT_FIELD_TYPES } from "../segmentRegistry";
import { effectiveSectionPalette, getValueAtDescriptorPath } from "../elementEditing";
import { persistedCssColorError, registryFieldError } from "../inspector/inspectorModel";
import { VisualColorControl, VisualSliderControl } from "../inspector/VisualSettingControls";

function finiteLimit(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
}

function rangeHelper(field) {
  const min = finiteLimit(field?.limits?.min);
  const max = finiteLimit(field?.limits?.max);
  if (min === undefined && max === undefined) return undefined;

  const unitMatch = String(field?.label || "").match(/\(([^)]+)\)\s*$/);
  const unit = unitMatch?.[1] ? ` ${unitMatch[1]}` : "";
  if (min !== undefined && max !== undefined) return `Permitido: ${min}–${max}${unit}`;
  if (min !== undefined) return `Mínimo: ${min}${unit}`;
  return `Máximo: ${max}${unit}`;
}

function fieldUnit(field) {
  const unitMatch = String(field?.label || "").match(/\(([^)]+)\)\s*$/);
  return unitMatch?.[1] || "";
}

function NumberField({ field, value, disabled, onCommit }) {
  const externalValue = value === undefined || value === null ? "" : String(value);
  const [draft, setDraft] = useState(externalValue);
  const [edited, setEdited] = useState(false);

  useEffect(() => {
    setDraft(externalValue);
    setEdited(false);
  }, [externalValue]);

  const min = finiteLimit(field?.limits?.min);
  const max = finiteLimit(field?.limits?.max);
  const step = finiteLimit(field?.limits?.step);

  const commitDraft = () => {
    if (!edited) return;

    if (draft === "") {
      setEdited(false);
      onCommit?.(field.path, "");
      return;
    }

    const parsed = Number(draft);
    if (!Number.isFinite(parsed)) {
      setDraft(externalValue);
      setEdited(false);
      return;
    }

    let normalized = parsed;
    if (min !== undefined) normalized = Math.max(min, normalized);
    if (max !== undefined) normalized = Math.min(max, normalized);

    const normalizedDraft = String(normalized);
    setDraft(normalizedDraft);
    setEdited(false);
    onCommit?.(field.path, normalized);
  };

  return (
    <TextField
      fullWidth
      size="small"
      type="number"
      label={field.label}
      value={draft}
      disabled={disabled}
      helperText={rangeHelper(field)}
      inputProps={{
        ...(min !== undefined ? { min } : {}),
        ...(max !== undefined ? { max } : {}),
        ...(step !== undefined ? { step } : {}),
      }}
      onChange={(event) => {
        setDraft(event.target.value);
        setEdited(true);
      }}
      onBlur={commitDraft}
      onKeyDown={(event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        event.target.blur();
      }}
    />
  );
}

function mobileColorFallback(field, form, descriptor) {
  const palette = effectiveSectionPalette(form, descriptor);
  const darkStyle = form?.navigation?.style === "style-3";
  const path = String(field?.path || "");
  const hamburgerColor = getValueAtDescriptorPath(form, "navigation.mobile.hamburgerColor", "") || (darkStyle ? palette.onSecondary : palette.secondary);
  const hamburgerBackground = getValueAtDescriptorPath(form, "navigation.mobile.hamburgerBackgroundColor", "") || (darkStyle ? "rgba(255,255,255,.08)" : palette.surface);

  if (path.endsWith("drawerBackgroundColor")) return palette.background;
  if (path.endsWith("drawerTextColor")) return palette.text;
  if (path.endsWith("drawerMutedTextColor")) return palette.mutedText;
  if (path.endsWith("drawerDividerColor")) return palette.divider;
  if (path.endsWith("itemHoverBackgroundColor")) return palette.background;
  if (path.endsWith("hamburgerColor")) return darkStyle ? palette.onSecondary : palette.secondary;
  if (path.endsWith("hamburgerBackgroundColor")) return darkStyle ? "rgba(255,255,255,.08)" : palette.surface;
  if (path.endsWith("hamburgerHoverColor")) return hamburgerColor;
  if (path.endsWith("hamburgerHoverBackgroundColor")) return hamburgerBackground;
  if (path.endsWith("hamburgerBorderColor")) return darkStyle ? "rgba(255,255,255,.2)" : palette.divider;
  return palette.text || "#000000";
}

export default function NavbarMobileElementEditor({ descriptor, form, disabled, onCommit }) {
  const fields = (descriptor?.fields || []).filter((field) => field?.editable !== false);
  return (
    <Stack spacing={1.1} sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)" }}>Los valores vacíos siguen heredando la configuración de escritorio o del diseño.</Typography>
      {fields.map((field) => {
        const value = getValueAtDescriptorPath(form, field.path, "");
        if (field.type === SEGMENT_FIELD_TYPES.BOOLEAN) {
          return (
            <FormControlLabel
              key={field.path}
              control={<Switch checked={value === true} disabled={disabled} onChange={(event) => onCommit?.(field.path, event.target.checked)} />}
              label={field.label}
              sx={{ m: 0, minHeight: 44 }}
            />
          );
        }
        if (field.type === SEGMENT_FIELD_TYPES.SELECT) {
          return (
            <FormControl key={field.path} fullWidth size="small" disabled={disabled}>
              <InputLabel>{field.label}</InputLabel>
              <Select label={field.label} value={value ?? ""} onChange={(event) => onCommit?.(field.path, event.target.value)}>
                {(field.options || []).map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
              </Select>
            </FormControl>
          );
        }
        if (field.type === SEGMENT_FIELD_TYPES.COLOR) {
          return (
            <VisualColorControl
              key={field.path}
              label={field.label}
              value={value}
              fallback={mobileColorFallback(field, form, descriptor)}
              disabled={disabled}
              inheritedLabel="Heredado"
              helperText={value ? "Color propio de la navegación móvil." : "Sin override, usa el color efectivo del diseño."}
              allowExactCss
              validateExactValue={(candidate) => registryFieldError(field, candidate) || persistedCssColorError(candidate, field.maxLength || 100)}
              exactValueMaxLength={field.maxLength || 100}
              onChange={(next) => onCommit?.(field.path, next)}
              onReset={() => onCommit?.(field.path, "")}
            />
          );
        }
        if (field.type === SEGMENT_FIELD_TYPES.NUMBER && field.control === "slider") {
          return (
            <VisualSliderControl
              key={field.path}
              label={field.label}
              value={value}
              min={field.limits?.min ?? 0}
              max={field.limits?.max ?? 100}
              step={field.limits?.step ?? 1}
              unit={fieldUnit(field)}
              disabled={disabled}
              helperText={field.helperText || rangeHelper(field)}
              nullable={field.nullable === true}
              fallbackValue={field.inheritedFallback}
              resetLabel={field.resetLabel || "Heredar"}
              onChange={(next) => onCommit?.(field.path, next)}
              onReset={() => onCommit?.(field.path, "")}
            />
          );
        }
        if (field.type === SEGMENT_FIELD_TYPES.NUMBER) {
          return <NumberField key={field.path} field={field} value={value} disabled={disabled} onCommit={onCommit} />;
        }
        return <TextField key={field.path} fullWidth size="small" type="text" label={field.label} value={value ?? ""} disabled={disabled} onChange={(event) => onCommit?.(field.path, event.target.value)} />;
      })}
    </Stack>
  );
}
